import React, { ReactNode } from 'react';
import styled, { css } from 'styled-components';

import { BUTTON_THEMES, CARD_WRAPPER_TYPES } from '../../../shared/constants';
import { IContentfulLink } from '../../../types/shared';

import PrimaryButton from '../../atoms/primary-button/PrimaryButton';
import SecondaryButton from '../../atoms/secondary-button/SecondaryButton';

import { DEQ_TYPES } from '../../../utils/deq';
import { getCtaLink } from '../../../utils/generic';

import mq from '../../../styles/breakpoints';
import buttonStyles, { CtaWrapper } from '../../../styles/buttonStyles';
import colors from '../../../styles/colors';
import { Body, Divider, H3 } from '../../../styles/generic';
import Spacings from '../../../styles/Spacings';

import { Orientation } from '../../../tokens';

interface Props {
  type: string;
  index: number;
  title: string;
  children: ReactNode;
  primaryLink?: IContentfulLink;
  secondaryLink?: IContentfulLink;
}

const Step = ({
  type,
  index,
  title,
  children,
  primaryLink,
  secondaryLink,
}: Props) => {
  const buttonDEQAttributes = {
    type: DEQ_TYPES.textLink,
    componentType: type,
  };
  const dividerOrientation =
    type === CARD_WRAPPER_TYPES.stepHorizontalWrapper
      ? Orientation.HORIZONTAL
      : Orientation.VERTICAL;

  return (
    <Container align={type} key={index} data-testid="step">
      <NumberWrapper>
        <Number data-testid="number">{index + 1}</Number>
      </NumberWrapper>
      <StyledDivider
        orientation={dividerOrientation}
        data-testid="step-line"
        className={type}
      />
      <TextContainer align={type}>
        <StepTitle data-testid="title">{title}</StepTitle>
        {children && (
          <Description align={type} data-testid="description">
            {children}
          </Description>
        )}
        {(primaryLink || secondaryLink) && (
          <StyledCtaWrapper align={type}>
            {primaryLink && (
              <StyledPrimaryButton
                type={primaryLink?.type}
                label={primaryLink.label || primaryLink.name}
                buttonTheme={primaryLink.theme || BUTTON_THEMES.highlight}
                to={getCtaLink(primaryLink)}
                iconPosition={primaryLink?.iconPosition}
                icon={primaryLink?.icon}
                deqComponentTitle={primaryLink.label}
                deqCustomAttributes={buttonDEQAttributes}
              />
            )}
            {secondaryLink && (
              <StyledSecondaryButton
                type={secondaryLink?.type}
                label={secondaryLink.label || secondaryLink.name}
                buttonTheme={secondaryLink.theme || BUTTON_THEMES.default}
                to={getCtaLink(secondaryLink)}
                iconPosition={secondaryLink?.iconPosition}
                icon={secondaryLink?.icon}
                deqComponentTitle={secondaryLink.label}
                deqCustomAttributes={buttonDEQAttributes}
                target={
                  secondaryLink.externalUrl || secondaryLink.asset
                    ? '_blank'
                    : '_self'
                }
              />
            )}
          </StyledCtaWrapper>
        )}
      </TextContainer>
    </Container>
  );
};

const Container = styled.div<{ align: string }>`
  align-items: flex-start;
  display: flex;
  margin-top: 14px;
  padding-bottom: 24px;
  position: relative;

  &:first-of-type {
    margin-top: 0;
  }

  &:last-of-type {
    padding-bottom: 0;

    hr {
      display: none;
    }
  }

  ${({ align }) =>
    align === CARD_WRAPPER_TYPES.stepHorizontalWrapper &&
    css`
      ${mq.from.breakpoint.M`
        padding-bottom: 0;
        height: 100%;
        flex-direction: column;
        align-items: center;
        margin-top: 0;

        &:last-of-type {
          hr {
            display: none;
          }
        }
      `}

      ${mq.from.breakpoint.L`
        padding-bottom: 0;

        &:nth-of-type(2n+2) {
          hr {
            display: block;
          }
        }

        &:last-of-type {
          hr {
            display: none;
          }
        }
      `}

      ${mq.from.breakpoint.XL`
        padding-bottom: 0;

        &:nth-of-type(3n+3) {
          hr {
            display: block;
          }
        }
        
        &:nth-of-type(4n+4) {
          hr {
            display: none;
          }
        }

        &:last-of-type {
          hr {
            display: none;
          }
        }
      `}
    `}
`;

const NumberCircle = css`
  background: linear-gradient(205.5deg, #009695 12.49%, #005e5d 87.51%);
  border-radius: ${({ theme }) => theme.radiuses.XXL};
  content: '';
  height: 28px;
  left: 50%;
  position: absolute;
  top: 50%;
  transform: translate(-50%, -50%);
  width: 28px;

  ${mq.from.breakpoint.M`
    height: 42px;
    width: 42px;
  `}

  ${mq.from.breakpoint.L`
    height: 46px;
    width: 46px;
  `}
`;

const NumberWrapper = styled.div`
  position: relative;

  &:before {
    ${NumberCircle};
  }
`;

const Number = styled.div`
  align-items: center;
  background: linear-gradient(225deg, #00c4bb 15.52%, #009185 87.26%);
  border-radius: ${({ theme }) => theme.radiuses.XXL};
  color: ${colors.white};
  display: flex;
  flex-shrink: 0;
  font-family: ${({ theme }) => theme.fontRegular};
  font-size: 16px;
  font-weight: 900;
  height: 24px;
  justify-content: center;
  line-height: 1;
  position: relative;
  width: 24px;

  ${mq.from.breakpoint.M`
    font-size: 28px;
    height: 36px;
    width: 36px;
  `}

  ${mq.from.breakpoint.L`
    height: 40px;
    width: 40px;
  `}
`;

export const StyledDivider = styled(Divider)<{ orientation: Orientation }>`
  bottom: 0;
  height: initial;
  left: 12px;
  position: absolute;
  top: 38px;
  width: 1px;

  ${({ orientation }) =>
    orientation === Orientation.VERTICAL &&
    css`
      ${mq.from.breakpoint.M`
        left: 18px;
        top: 52px;
      `}

      ${mq.from.breakpoint.L`
        left: 20px;
        top: 56px;
      `}
    `}

  ${({ orientation }) =>
    orientation === Orientation.HORIZONTAL &&
    css`
      ${mq.from.breakpoint.M`
        top: 18px;
        left: calc(50% + ${Spacings.XL});
        height: 1px;
        width: calc(100% - ${Spacings.XL} - ${Spacings.S})
      `};

      ${mq.from.breakpoint.L`
        top: 21px;
      `}

      ${mq.from.breakpoint.XL`
        width: calc(100% - ${Spacings.XL})
      `}
    `}
`;

const TextContainer = styled.div<{ align: string }>`
  margin: 0 0 0 18px;
  order: 1;

  ${({ align }) =>
    align === CARD_WRAPPER_TYPES.stepVerticalWrapper &&
    css`
      ${mq.from.breakpoint.M`
        padding-top: 6px;
        margin: 0 0 0 28px;
      `}

      ${mq.from.breakpoint.L`
        margin: 0 0 0 40px;
      `}
    `}

  ${({ align }) =>
    align === CARD_WRAPPER_TYPES.stepHorizontalWrapper &&
    css`
      ${mq.from.breakpoint.M`
        margin: 32px 0 0;
        text-align: center;
        display: flex;
        flex: 1 1 0%;
        flex-direction: column;
      `}

      ${mq.from.breakpoint.L`
        margin: 32px 0 0;
      `}
    `}
`;

const StepTitle = styled(H3)`
  color: ${colors.abnBlack};
  margin-bottom: 8px;
  margin-top: 0;
`;

const Description = styled(Body)<{ align: string }>`
  color: ${colors.abnNeutral500};

  p {
    margin: 0;
  }
`;

const StyledCtaWrapper = styled(CtaWrapper)<{ align: string }>`
  margin-top: 24px;

  ${({ align }) =>
    align === CARD_WRAPPER_TYPES.stepHorizontalWrapper &&
    css`
      ${mq.from.breakpoint.M`
        justify-content: center;
        align-items: center;
      `}
    `}
`;

const StyledPrimaryButton = styled(PrimaryButton)`
  ${buttonStyles.primaryButtonPosition}
`;

const StyledSecondaryButton = styled(SecondaryButton)`
  ${buttonStyles.secondaryButtonPosition}
`;

export default Step;
